// @workflow platform.viewer
/**
 * What the 3D scene shows: the shared spatial-focus scene, isolation, show / hide, colours, and
 * ghost mode and the full reset. Every operation forwards to today's implementation (forgeService, the main
 * extension, useSpatialFocus) unchanged; the comment on each names it.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { loadedMainExtension } from './readiness.js';
import { sameId, childDbIds } from './ids.js';

/** Ghost mode's two styles, as the assistant's set_ghosting names them (viewerContract.js GHOSTING_STYLE). */
const GHOSTING_WIREFRAME = 'wireframe';
const GHOSTING_SOLID = 'solid';

/** A render-plan input with nothing in it, for a resolve that came back empty. */
const NOTHING = Object.freeze([]);

/**
 * The statuses this module answers itself (the rest come from useSpatialFocus.show). RENDERED is
 * `frameScope`'s "the plan was framed", the word useSpatialFocus.show uses for its own success.
 */
export const SCENE_STATUS = Object.freeze({ UNAVAILABLE: 'unavailable', ABORTED: 'aborted', FAILED: 'failed', RENDERED: 'rendered' });

/**
 * Every tag the viewer can map to elements: the store's tag lists, then the automatic tags
 * (the mapping viewerHost.readSelection used; store getters `getAllTags`, `GET_ALL_EXISTING_AUTOMATIC_TAGS`).
 */
export function tagsInStore(store) {
  return [...(store.getters.getAllTags || []), ...(store.getters.GET_ALL_EXISTING_AUTOMATIC_TAGS || [])];
}

/** Every tag in the store with its element ids: the tag id to dbId mapping. */
export function tagOwnersIn(store) {
  return tagsInStore(store).map((tag) => ({ id: tag && tag.id, dbIds: childDbIds(tag) }));
}

/** @param {object} ctx see core.js `createViewer` */
export function createSceneOps(ctx) {
  const { store, deps, current, service, warnNoViewer } = ctx;
  /** The legacy path loads the main extension when missing (forgeService.getMainExtension). */
  const legacyExtension = () => service().getMainExtension();

  /**
   * mainExtension.isolateDbIds (fits the view; an empty list ends isolation) or, with
   * `fit: false`, isolateDbIdsWithoutFitView. The guard and toast are forgeService.isolateTag's.
   * @returns {boolean} false when there is no viewer or main extension
   */
  function isolate(dbIds, { fit = true, model = null } = {}) {
    if (!current()) {
      warnNoViewer();
      return false;
    }
    const ext = legacyExtension();
    if (!ext) return false;
    if (fit) ext.isolateDbIds(dbIds, model);
    else ext.isolateDbIdsWithoutFitView(dbIds, model);
    return true;
  }

  /**
   * The server's answer for `scope` and the render plan built from it for the loaded models,
   * without painting anything. `status` is set only when there is nothing to go on.
   * @returns {Promise<{ status: string|null, viewer?: object, result?: object, plan?: object }>}
   */
  async function resolveScope({ scope, encoding, signal }) {
    const viewer = current();
    if (!viewer) return { status: SCENE_STATUS.UNAVAILABLE };
    const response = await deps.resolveSpatialFocus({ scope }, { signal: signal || undefined });
    const result = response ? response.data : null;
    if (signal && signal.aborted) return { status: SCENE_STATUS.ABORTED };
    if (!result) return { status: SCENE_STATUS.FAILED };
    const plan = deps.buildRenderPlan({
      occurrences: result.occurrences || NOTHING,
      subjects: result.subjects || NOTHING,
      addresses: result.addresses || NOTHING,
      encoding,
      loadedModels: deps.listLoadedModels(viewer),
    });
    return { status: null, viewer, result, plan };
  }

  return {
    isolate,
    /** useSpatialFocus().show on the current viewer (the one scene authority; ViewerTaskColoring, dashboards). */
    showScene(input) {
      return deps.spatialFocus.show({ ...input, viewer: current() });
    },
    /** useSpatialFocus().clear on the current viewer; no owner clears whoever painted it. */
    clearScene({ owner, reason } = {}) {
      return deps.spatialFocus.clear({ owner, reason, viewer: current() });
    },
    /**
     * Resolve first, then show that same answer (the CustomDashboard.vue exact-focus pattern), so
     * the caller can tell which subjects reached the screen: `plan` is built here from the loaded
     * models and is the one `show` paints in the same turn.
     * @returns {Promise<{ status: string, subjects?: object[], plan?: object }>}
     */
    async resolveAndShowScope({ owner, scope, encoding, signal }) {
      const { status, viewer, result, plan } = await resolveScope({ scope, encoding, signal });
      if (status) return { status };
      // Swap from the previous scene straight to this one, like task colouring (ViewerTaskColoring.vue).
      const outcome = await deps.spatialFocus.show({ owner, scope, encoding, viewer, swapInPlace: true, resolvedResult: result });
      return { status: outcome && outcome.status, subjects: result.subjects || NOTHING, plan };
    },
    /**
     * Frame a scope without isolating it: the zoom to several tags (actions/zoomToTags.js). Resolved
     * as resolveAndShowScope resolves, then the plan's target elements are shown
     * (mainExtension.showDbIds, what "Zoom to tag in 3D" does for one tag's elements) and framed as
     * the spatial-focus scene frames them (forgeSpatialFocusAdapter `fitSpatialRenderPlan`). Nothing
     * is hidden, greyed or coloured, and the shared scene is not touched.
     * @returns {Promise<{ status: string, subjects?: object[], plan?: object }>} SCENE_STATUS.RENDERED once framed
     */
    async frameScope({ scope, signal }) {
      const { status, viewer, result, plan } = await resolveScope({ scope, encoding: null, signal });
      if (status) return { status };
      const ext = legacyExtension();
      if (ext) (plan.pulseTargets || NOTHING).forEach((bucket) => ext.showDbIds(bucket.dbIds, bucket.model));
      deps.fitRenderPlan(viewer, plan);
      return { status: SCENE_STATUS.RENDERED, subjects: result.subjects || NOTHING, plan };
    },
    /**
     * forgeService.isolateTag for a tag in the store, which also picks the tag's own model for a
     * harvested model tag. A tag without `getAllDbIds` (an automatic-tag record) isolates its children.
     * @returns {boolean} false when the tag is not loaded or there is no viewer
     */
    isolateTag(tagId, { fit = true } = {}) {
      const tag = tagsInStore(store).find((t) => t && sameId(t.id, tagId));
      if (!tag) return false;
      if (typeof tag.getAllDbIds !== 'function') return isolate(childDbIds(tag), { fit });
      if (!current()) {
        warnNoViewer();
        return false;
      }
      service().isolateTag(tag, fit);
      return true;
    },
    /** mainExtension.showDbIds. */
    show(dbIds, model = null) {
      if (!current()) return false;
      const ext = legacyExtension();
      if (!ext) return false;
      ext.showDbIds(dbIds, model);
      return true;
    },
    /** The Forge viewer's own hide (no legacy wrapper exists). */
    hide(dbIds, model = null) {
      const viewer = current();
      if (!viewer) return false;
      viewer.hide(dbIds, model);
      return true;
    },
    /** mainExtension.endIsolation, only when the extension is already loaded. */
    endIsolation() {
      const ext = loadedMainExtension(current());
      if (!ext) return false;
      ext.endIsolation();
      return true;
    },
    /**
     * Theme element groups, as forgeService.showTagsByIdsWithColorAndAnimation does without the
     * animation: record the themed ids (SET_THEMED_DB_IDS, so `resetColours` clears them), then
     * forgeUtils.setPermeativeElementsTheme per group.
     * @param {Array<{ dbIds: number[], color: string }>} groups `color` as '#rrggbb'
     */
    colour(groups) {
      if (!current()) return false;
      const valid = (groups || []).filter((g) => g && g.color && Array.isArray(g.dbIds));
      store.commit('SET_THEMED_DB_IDS', valid.flatMap((g) => g.dbIds));
      valid.forEach((g) => deps.themeElements(g.dbIds, deps.toThemingColor(g.color)));
      return true;
    },
    /** forgeService.resetThemingColors (clears only when themed ids are recorded). */
    resetColours() {
      service().resetThemingColors();
    },
    /**
     * Quick Settings' ghost mode (QuickSettingsButton.vue isGhostingOn / isGhostingWireframe): the
     * store's SET_GHOSTING, then forgeService.updateGhosting once, as the switch does. `style`
     * 'wireframe' | 'solid', or null to keep the current one.
     * @returns {{ on: boolean, style: string }|null} what the viewer now shows, or null with no viewer
     */
    setGhosting({ on, style = null }) {
      if (!current()) return null;
      const wireframe = style === null ? store.state.isGhostingWireframe !== false : style === GHOSTING_WIREFRAME;
      store.commit('SET_GHOSTING', { on: !!on, wireframe });
      service().updateGhosting();
      return { on: !!on, style: wireframe ? GHOSTING_WIREFRAME : GHOSTING_SOLID };
    },
    /**
     * A clean viewer, whoever painted it (viewerHost.reset, unchanged): the shared scene, the
     * isolation, the colours. Throws when the main extension is not loaded, as it did.
     */
    reset() {
      deps.spatialFocus.clear({ viewer: current() });
      loadedMainExtension(current()).endIsolation();
      service().resetThemingColors();
    },
  };
}
